/* eslint-disable jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events,jsx-a11y/label-has-for */
import { memo, useState } from 'react';
import InputGroup from 'react-bootstrap/InputGroup';

import { Form, StringInput, ResetButton, SubmitButton } from '../forms';
import type { StringInputProps } from '../forms/StringInput';

const fireChangeEvent = (input: HTMLInputElement | null) => {
  if (input) {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

    nativeInputValueSetter.call(input, '');

    const ev = new Event('change', { bubbles: true });
    input.dispatchEvent(ev);
    input.focus();
  }
};

/**
 * Быстрый поиск: инпут + кнопки «сброс»/«лупа» поверх отдельной мини-формы (Form).
 * Enter — сабмит, Escape — сброс.
 *
 * `value` — только начальное значение (initialValues мини-формы), НЕ контролируемый пропс.
 */
export interface QuickSearchProps extends Omit<StringInputProps, 'onChange'> {
  /** (event, value) — ввод; value — текущее значение поля. */
  onChange?: (event: any, value?: string) => void;
  /** (value) — сброс крестииком (всегда ''). */
  onClear?: (value?: string) => void;
  /** (value) — сабмит (Enter/лупа). */
  onSubmit?: (value: any) => void;
  /** Скрыть кнопку-лупу. @default false */
  hideSearchButton?: boolean;
}

const QuickSearch = (props: QuickSearchProps) => {
  const [input, setInput] = useState<HTMLInputElement | null>(null);
  const {
    name,
    value,
    disabled,
    onChange,
    onClear,
    onSubmit,
    hideSearchButton = false,
    ...rest
  } = props;

  const onReset = () => {
    fireChangeEvent(input);

    if (typeof onClear === 'function') {
      onClear('');
    }
  };

  return (
    <Form
      initialValues={ { [name]: value } }
      onReset={ onReset }
      onSubmit={ (values) => onSubmit(values[name]) }
    >
      { ({ values, handleChange, handleReset, handleSubmit }) => (
        <div className='quick-search'>
          <StringInput
            { ...rest }
            ref={ setInput as any } /* Dispatch<SetStateAction<HTMLInputElement>> инвариантен
                                       LegacyRef<HTMLElement> — типы конфликтуют, runtime 1:1 */
            name={ name }
            disabled={ disabled }
            onChange={ (event) => {
              handleChange(event);

              if (typeof onChange === 'function') {
                onChange(event, event.target?.value);
              }
            } }
            onKeyUp={ (event) => {
              if (event.key === 'Enter') {
                handleSubmit();
              } else if (event.key === 'Escape') {
                handleReset();
              }
            } }
          >
            <InputGroup.Append>
              { values[name] && (
                <ResetButton variant='link' icon='times' disabled={ disabled } />
              ) }

              { (!hideSearchButton) && (<SubmitButton variant='link' icon='search' disabled={ disabled } isSpinnerOff />) }
            </InputGroup.Append>
          </StringInput>
        </div>
      ) }
    </Form>
  );
};

export default memo(QuickSearch);
